import React from "react";
import classNames from "classnames";
//@ts-ignore - json-logic-js has't got types
import jsonLogic from "json-logic-js";
import FormHead from "./FormHead";
import FormSubmit from "./FormSubmit";
import FormComponentRenderer from "../FormComponentRenderer";
import FormRefs from "../../interfaces/Refs";
import { Component, Components } from "../../interfaces/Form/Component";
import {
  FormComponents,
  FormState,
  FormStateErrorsVariable,
  FormStateDirty,
  FormStateDisabled,
  FormStateHidden,
  FormStateHiddenVariable,
  FormStateValue,
  FormStateValues,
  FormStateValueVariable,
  FormStateVariables,
  FormStateErrors,
  FormStateDisabledVariable,
  FormStateError
} from "../../interfaces/Form/State";
import { FormComponent } from "../../interfaces/Form/FormComponent";
import { LayoutComponent } from "../../interfaces/Form/LayoutComponent";
import {
  VariableComponent,
  VariableIDHierarchy
} from "../../interfaces/Form/VariableComponent";
import {
  scrollToRef,
  initValueObject,
  areUploadsEmpty,
  userFilesToObject,
  scopeToID,
  updateObjectByPath,
  normalizeDynamicFormID
} from "../../utils/utils";
import {
  isLayoutComponent,
  isWidget,
  hasConditional,
  getComponentDataType,
  isFormComponent
} from "../../utils/components";
import {
  stateAddFiles,
  stateSetFilesError,
  stateSetUploadedFile,
  stateRemoveFile
} from "../../utils/state";
import {
  applyJsonLogic,
  validateComponent as validateFormComponent
} from "../../validations/validations";
import { FORM_HEAD_ID, FORM_SUBMIT_ID } from "../../constants";
import FormContext from "../../context/FormContext";
import { File as FileI } from "../../interfaces/File";
import { AddFiles, CancelFile } from "../../interfaces/FileUpload";
import Submit from "../../interfaces/Submit";
import Uploads from "../../interfaces/Uploads";
import LoaderOverlay from "../../components/_common/LoaderOverlay";
import LoadingMessages from "../../components/_common/LoadingMessages";

interface LayoutItem {
  children?: LayoutItem[];
  id: string;
}

interface Props {
  classNames?: {
    description?: string;
    form?: string;
    title?: string;
  };
  formSlug: string;
  components: Components;
  description?: string;
  error: string;
  isLoading: boolean;
  layout: LayoutItem[];
  onAddFiles: AddFiles;
  onCancelFile: CancelFile;
  onSubmit: Submit;
  onSubmitReset: () => void;
  organization: any;
  schemaID: string;
  specID: string;
  specsValues: any;
  success: string;
  title?: string;
  uploads: Uploads;
}

class FormDynamic extends React.Component<Props, FormState> {
  private componentRefs: any = {};

  constructor(props: Props) {
    super(props);
    this.state = {
      components: props.components,
      dirty: {},
      disabled: {},
      errors: {},
      formComponents: {},
      hidden: {},
      hiddenParent: {},
      templateComponents: {},
      values: {},
      variables: {}
    };
  }

  filterNonFormComponents(components: Components) {
    const out: FormComponents = {};
    Object.keys(components).forEach((key: string) => {
      if (!isFormComponent(components[key])) return;
      out[key] = components[key] as FormComponent;
    });
    return out;
  }

  // filter out template components, because they do not stand alone
  getStandAloneComponents(components: Components) {
    const templatesIDs = this.getVariablesTemplates(components);
    return Object.keys(components).reduce(
      (standAloneComponents: Components, key: string) => {
        const component = components[key];
        if (templatesIDs.includes(component.props.id))
          return standAloneComponents;
        standAloneComponents[key] = component;
        return standAloneComponents;
      },
      {}
    );
  }

  getTemplateComponents(components: Components) {
    const templatesIDs = this.getVariablesTemplates(components);
    return Object.keys(components).reduce(
      (templateComponents: Components, key: string) => {
        if (templatesIDs.includes(key)) {
          templateComponents[key] = components[key];
        }
        return templateComponents;
      },
      {}
    );
  }

  componentDidMount() {
    const allComponents = this.props.components;
    const templateComponents = this.getTemplateComponents(allComponents);
    const standAloneComponents = this.getStandAloneComponents(allComponents);
    const formComponents = this.filterNonFormComponents(standAloneComponents);
    const values = this.getDefaultValues(templateComponents);
    const variables = this.getDefaultVariables(formComponents, values);

    this.setRefs();
    this.setState(
      {
        ...this.state,
        components: standAloneComponents,
        formComponents,
        templateComponents,
        values,
        variables
      },
      () => {
        const { disabled, hidden, hiddenParent } = this.checkConditionals();
        const { values } = this.getCalculatedValues();
        this.setState({
          ...this.state,
          disabled,
          hidden,
          hiddenParent,
          values
        });
      }
    );
  }

  validate() {
    const errors = this.getErrors();
    const isValid = Object.keys(errors).length === 0;
    this.setState(
      {
        ...this.state,
        errors
      },
      () => {
        if (!isValid) {
          this.scrollToError();
        }
      }
    );
    return isValid;
  }

  flatStateToObject = (values: FormStateValues) => {
    let out: FormStateValues = {};
    Object.keys(values).forEach((key: string) => {
      out = updateObjectByPath(out, "/" + key, values[key]);
    });
    return out;
  };

  submit(shouldSign?: boolean) {
    if (this.isCurrentlyUploading() || !this.validate()) return;
    this.props.onSubmit(
      this.props.formSlug,
      this.props.organization.id,
      this.flatStateToObject(
        this.filterHiddenValues(
          this.filterNonPersistingValues(
            this.state.values,
            this.state.formComponents
          )
        )
      ),
      shouldSign
    );
  }

  handleSubmit = (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    this.submit(); //for now only without signing
  };

  scrollToError() {
    const componentWithErrorId = this.getFirstComponentWithError();
    if (componentWithErrorId) {
      if (this.componentRefs[componentWithErrorId]?.current.scrollToError) {
        return this.componentRefs[
          componentWithErrorId
        ]?.current.scrollToError();
      } else {
        scrollToRef(
          this.componentRefs[componentWithErrorId],
          componentWithErrorId
        );
        return true;
      }
    }
    return false;
  }

  isCurrentlyUploading() {
    return !areUploadsEmpty(this.props.uploads);
  }

  handleAddFiles = (id: string, files: FileI[]) => {
    const uploadingFiles = userFilesToObject(files);
    this.setState(stateAddFiles(this.state, id, uploadingFiles));
    this.props.onAddFiles(id, files);
  };

  handleUploadDone = (id: string, file: FileI | undefined, error?: string) => {
    if (!file) {
      this.setState(stateSetFilesError(this.state, id, error));
      return;
    }
    this.setState(stateSetUploadedFile(this.state, id, file));
  };

  handleUploadCancel = (id: string, fileID: string) => {
    this.setState(stateRemoveFile(this.state, id, fileID));
  };

  getDefaultVariables(components: FormComponents, values: FormStateValues) {
    const variables: FormStateVariables = {};
    Object.keys(components).forEach((componentID: string) => {
      const component = components[componentID];
      if (!isWidget(component, "Variable")) return;
      variables[component.props.id] = this.getVariableDefaultRepeat(
        component as VariableComponent,
        values
      );
    });
    return variables;
  }

  getVariableDefaultRepeat = (
    variable: VariableComponent,
    values: FormStateValues
  ) => {
    if (values[variable.props.id] !== undefined)
      return Object.keys(values[variable.props.id] as FormStateValueVariable)
        .length;
    if (variable.props.repeat?.default !== undefined)
      return variable.props.repeat?.default;
    return 1;
  };

  getVariablesTemplates(components: Components) {
    let componentIDs: string[] = [];
    Object.keys(components).forEach((componentID: string) => {
      const component = components[componentID];
      if (!isWidget(component, "Variable")) return;
      componentIDs = componentIDs.concat(
        this.getComponentsInTree(component, components)
      );
    });
    return componentIDs;
  }

  getComponentsInTree(component: Component, components: Components) {
    let IDs: string[] = [];
    component.props.components &&
      component.props.components.forEach((childID: string) => {
        IDs.push(childID);
        IDs = IDs.concat(
          this.getComponentsInTree(components[childID], components)
        );
      });
    return IDs;
  }

  getComponentDefaultValue(
    component: Component,
    templateComponents: Components
  ) {
    if (isLayoutComponent(component)) return;
    if (isWidget(component, "Variable")) {
      return this.getVariableDefaultValue(
        component as VariableComponent,
        templateComponents
      );
    }
    const formComponent = component as FormComponent;
    return initValueObject(
      formComponent.props.value ?? formComponent.props.defaultValue ?? "",
      formComponent.props
    );
  }

  setRefs() {
    this.componentRefs = Object.keys(this.state.components).reduce(
      (allRefs: FormRefs, key: string) => {
        allRefs[key] = React.createRef();
        return allRefs;
      },
      {}
    );
  }

  getDefaultValues(templateComponents: Components) {
    const values: FormStateValues = {};
    Object.values(this.state.components).forEach((component: Component) => {
      if (isLayoutComponent(component)) return;
      values[component.props.id] = this.getComponentDefaultValue(
        component,
        templateComponents
      );
    });
    return values;
  }

  getVariableDefaultValue(
    component: VariableComponent,
    templateComponents: Components
  ) {
    const repeat = component.props.value
      ? component.props.value.length
      : component.props.repeat?.default;
    if (!repeat) return;
    return new Array(repeat).fill(
      this.getVariableLevelDefaultValues(component.props.id, templateComponents)
    );
  }

  getVariableLevelDefaultValues(id: string, templateComponents: Components) {
    const newLevelValues: FormStateValues = {};
    this.state.components[id].props.components?.forEach((key: string) => {
      newLevelValues[key] = this.getComponentDefaultValue(
        templateComponents[key],
        templateComponents
      );
    });
    return newLevelValues;
  }

  getVariableInnerComponents(
    component: LayoutComponent,
    components: Components
  ) {
    let componentIDs: string[] = [];
    Object.values(component.props.components).forEach((id: string) => {
      componentIDs = componentIDs.concat(
        this.flattenComponentsTree(id, components)
      );
    });
    return componentIDs;
  }

  flattenComponentsTree(componentID: string, components: Components) {
    const children = components[componentID].props.components;
    let childrenIDs: string[] = [];
    if (!isWidget(components[componentID], "Variable") && children) {
      children.forEach(id => {
        childrenIDs = childrenIDs.concat(
          this.flattenComponentsTree(id, components)
        );
      });
    }
    childrenIDs.push(componentID);
    return childrenIDs;
  }

  filterNonPersistingValues(values: FormStateValues, components: Components) {
    const outValues: FormStateValues = { ...values };
    Object.keys(outValues).forEach((key: string) => {
      if (components[key].props.persistent === false) delete outValues[key];
    });
    return outValues;
  }

  filterHiddenValues(values: FormStateValues) {
    const outValues: FormStateValues = { ...values };
    Object.keys(outValues).forEach((key: string) => {
      if (this.state.hidden[key] || this.state.hiddenParent[key])
        delete outValues[key];
    });
    return outValues;
  }

  validateComponent(
    component: FormComponent,
    values: FormStateValues,
    variables: FormStateVariables,
    hidden: FormStateHidden,
    hiddenParent: FormStateHidden
  ) {
    if (isWidget(component, "Variable")) {
      return this.validateVariableComponent(
        component as VariableComponent,
        values,
        variables,
        hidden,
        hiddenParent
      );
    } else if (component.props.customForm) {
      return this.componentRefs[component.props.id].current.validate();
    } else {
      return validateFormComponent(
        getComponentDataType(component),
        component.props.ui.widget,
        values && values[component.props.id],
        component.props.validate,
        values
      );
    }
  }

  validateVariableComponent(
    variable: VariableComponent,
    values: FormStateValues,
    variables: FormStateVariables,
    hidden: FormStateHidden,
    hiddenParent: FormStateHidden
  ) {
    const errors: FormStateErrorsVariable = { self: undefined, levels: {} };
    let hasError = false;
    const variableValues = values[variable.props.id] as FormStateValueVariable;
    for (let i = 0; i < this.state.variables[variable.props.id]; i++) {
      errors.levels[i] = {};
      const hasLevelError = variable.props.components.reduce(
        (hasLevelError: boolean, componentID: string) => {
          const component = this.state.templateComponents[componentID];
          const levelValues = variableValues ? variableValues[i] : {};
          if (isLayoutComponent(component)) return hasLevelError;
          const componentError = this.validateComponent(
            component as FormComponent,
            levelValues,
            variables,
            hidden,
            hiddenParent
          );
          if (componentError) {
            errors.levels[i][componentID] = componentError;
            return true;
          }
          return hasLevelError;
        },
        false
      );
      if (!hasError && hasLevelError) hasError = true;
    }
    return hasError ? errors : undefined;
  }

  getErrors() {
    let errors: FormStateErrors = {};
    Object.keys(this.state.formComponents).forEach((id: string) => {
      const component = this.state.formComponents[id];
      if (
        !this.shouldValidateComponent(
          component,
          this.state.hidden,
          this.state.hiddenParent
        )
      )
        return;
      const error = this.validateComponent(
        component,
        this.state.values,
        this.state.variables,
        this.state.hidden,
        this.state.hiddenParent
      );
      if (error) {
        errors[id] = error;
        return;
      }
    });
    return errors;
  }

  shouldValidateComponent(
    component: FormComponent,
    stateHidden: FormStateHidden,
    stateHiddenParent: FormStateHidden
  ) {
    if (isWidget(component, "Variable")) {
      return (
        !component.props.readonly &&
        !(stateHidden[component.props.id] as FormStateHiddenVariable)?.self &&
        !(stateHiddenParent[component.props.id] as FormStateHiddenVariable)?.self
      );
    }
    return (
      !component.props.readonly &&
      !stateHidden[component.props.id] &&
      !stateHiddenParent[component.props.id]
    );
  }

  checkConditionals() {
    const out: {
      disabled: FormStateDisabled;
      hidden: FormStateHidden;
      hiddenParent: FormStateHidden;
    } = {
      disabled: {},
      hidden: {},
      hiddenParent: {}
    };
    Object.keys(this.state.components).forEach((key: string) => {
      const component = this.state.components[key];
      let conditionals;
      if (isWidget(component, "Variable")) {
        conditionals = this.checkVariableConditionals(
          component as VariableComponent,
          this.state.values[component.props.id] as FormStateValueVariable
        );
      } else {
        conditionals = this.checkComponentConditionals(
          component as FormComponent,
          this.state.values
        );
      }
      out.disabled[key] = conditionals.disabled;
      out.hidden[key] = conditionals.hidden;
      out.hiddenParent = { ...out.hiddenParent, ...conditionals.hiddenParent };
    });
    return out;
  }

  isParentHidden(component: Component, hiddenParent: FormStateHidden) {
    if (!hiddenParent[component.props.id]) return false;
    if (typeof hiddenParent[component.props.id] === "boolean")
      return hiddenParent[component.props.id];
    if (isWidget(component, "Variable"))
      return (hiddenParent[component.props.id] as FormStateHiddenVariable).self;
    return false;
  }

  checkVariableConditionals(
    variable: VariableComponent,
    values: FormStateValueVariable
  ) {
    const out: {
      disabled: FormStateDisabledVariable;
      hidden: FormStateHiddenVariable;
      hiddenParent: FormStateHiddenVariable;
    } = {
      disabled: { self: undefined, levels: {} },
      hidden: { self: undefined, levels: {} },
      hiddenParent: { self: undefined, levels: {} }
    };
    for (let i = 0; i < this.state.variables[variable.props.id]; i++) {
      out.disabled.levels[i] = {};
      out.hidden.levels[i] = {};
      out.hiddenParent.levels[i] = {};
      this.state.components[variable.props.id].props.components?.forEach(
        (componentID: string) => {
          const component = this.state.templateComponents[componentID];
          const conditionals = this.checkComponentConditionals(
            component,
            this.trimObjectKeys(values[i], variable.props.id)
          );
          out.disabled.levels[i] = {
            ...(out.disabled.levels[i] as FormStateDisabled),
            [componentID]: conditionals.disabled
          };
          out.hidden.levels[i] = {
            ...(out.hidden.levels[i] as FormStateHidden),
            [componentID]: conditionals.hidden
          };
          out.hiddenParent.levels[i] = {
            ...(out.hiddenParent.levels[i] as FormStateHidden),
            ...conditionals.hiddenParent
          };
        }
      );
    }
    return out;
  }

  checkComponentConditionals(component: Component, values: FormStateValues) {
    const out: {
      disabled: boolean;
      hidden: boolean;
      hiddenParent: FormStateHidden;
    } = {
      disabled: false,
      hidden: false,
      hiddenParent: {}
    };
    if (hasConditional(component)) {
      const { disabled, hidden } = component.props.conditional;
      if (disabled) {
        out.disabled = jsonLogic.apply(disabled, values);
      }
      if (hidden) {
        const isHidden: boolean = jsonLogic.apply(hidden, values);
        out.hidden = isHidden;
        if (this.state.hiddenParent[component.props.id]) {
          out.hiddenParent[component.props.id] = true;
        } else {
          this.flattenComponentsTree(
            component.props.id,
            this.state.components
          ).forEach((id: string) => {
            if (id === component.props.id) return; // set only children components for parentHidden, not parent itself
            out.hiddenParent[id] = isHidden;
          });
        }
      }
    }
    return out;
  }

  trimObjectKeys<T>(object: { [key: string]: T }, stringToTrim?: string) {
    if (!stringToTrim) return object;
    const out: { [key: string]: T } = {};
    const trimLength = stringToTrim.length;
    Object.keys(object).forEach((key: string) => {
      out[key.slice(trimLength, key.length)] = object[key];
    });
    return out;
  }

  setHidden(id: string, isHidden: boolean) {
    this.setState({
      ...this.state,
      hidden: {
        ...this.state.hidden,
        [id]: isHidden
      }
    });
  }

  setError = (id: string, error?: string) => {
    this.setState({
      ...this.state,
      errors: {
        ...this.state.errors,
        [id]: error
      }
    });
  };

  getFirstComponentWithError() {
    return Object.keys(this.state.errors).find(
      (key: string) => this.state.errors[key]
    );
  }

  getCalculatedValues(ignore?: string) {
    const newValues: FormStateValues = {};
    const dirtyValues: FormStateDirty = {};
    Object.keys(this.state.formComponents).forEach((key: string) => {
      const component = this.state.formComponents[key];
      if (isLayoutComponent(component)) {
        return;
      }
      if (this.state.dirty[key] || ignore === component.props.id) {
        newValues[key] = this.state.values[key];
        if (this.state.values[key]) dirtyValues[key] = true;
        return;
      }
      newValues[key] = component.props.calculate
        ? applyJsonLogic(component.props.calculate, this.state.values)
        : this.state.values[key];
    });
    return { dirty: dirtyValues, values: newValues };
  }

  handleChange = (
    id: string,
    value: FormStateValue,
    error?: FormStateError
  ) => {
    const idNorm = normalizeDynamicFormID(id);
    this.resetSubmit();
    this.setState(
      {
        ...this.state,
        errors: {
          ...this.state.errors,
          [idNorm]: error
        },
        values: {
          ...this.state.values,
          [idNorm]: value
        }
      },
      () => {
        // make sure to call that after the change in state is complete, because the new state values will be used during refresh
        this.refresh(idNorm);
      }
    );
  };

  resetSubmit() {
    if (this.props.success || this.props.error) {
      this.props.onSubmitReset();
    }
  }

  refresh(id?: string) {
    const { disabled, hidden, hiddenParent } = this.checkConditionals();
    const { dirty, values } = this.getCalculatedValues(id);
    this.setState({
      ...this.state,
      dirty,
      disabled,
      hidden,
      hiddenParent,
      values
    });
  }

  handleVariableRepeatChange = (
    variableID: string,
    newRepeat: number,
    indexRemoved: number | undefined
  ) => {
    const id = normalizeDynamicFormID(variableID);
    const newValues = this.state.values[id]
      ? this.rebuildVariableValues(
          this.state.values[id] as FormStateValueVariable,
          indexRemoved
        )
      : undefined;
    const newErrors = this.state.errors[id]
      ? this.rebuildVariableErrors(
          this.state.errors[id] as FormStateErrorsVariable,
          indexRemoved
        )
      : undefined;
    if (newValues && indexRemoved === undefined) {
      // when adding new level, make sure to set it's default values
      newValues[newRepeat - 1] = this.getVariableLevelDefaultValues(
        id,
        this.state.templateComponents
      );
    }
    this.setState(
      {
        ...this.state,
        errors: {
          ...this.state.errors,
          [id]: newErrors
        },
        values: {
          ...this.state.values,
          [id]: newValues
        },
        variables: {
          ...this.state.variables,
          [id]: newRepeat
        }
      },
      () => {
        const { disabled, hidden, hiddenParent } = this.checkConditionals();
        const { dirty, values } = this.getCalculatedValues();
        this.setState({
          ...this.state,
          dirty,
          disabled,
          hidden,
          hiddenParent,
          values
        });
      }
    );
  };

  rebuildVariableValues(
    values: FormStateValueVariable,
    indexRemoved: number | undefined
  ) {
    if (indexRemoved === undefined) return values;
    const oldValues = { ...values };
    delete oldValues[indexRemoved];
    const newValues: FormStateValueVariable = {};
    Object.keys(oldValues).forEach((oldIndex, newIndex: number) => {
      newValues[newIndex] = oldValues[oldIndex];
    });
    return newValues;
  }

  rebuildVariableErrors(
    errors: FormStateErrorsVariable,
    indexRemoved: number | undefined
  ) {
    if (indexRemoved === undefined) return errors;
    const oldErrors = { ...errors, levels: { ...errors.levels } };
    delete oldErrors.levels[indexRemoved];
    const newErrors: FormStateErrorsVariable = { ...errors, levels: {} };
    Object.keys(oldErrors.levels).forEach((oldIndex, newIndex: number) => {
      newErrors.levels[newIndex] = oldErrors.levels[oldIndex];
    });
    return newErrors;
  }

  reset() {
    const values = this.getDefaultValues(this.state.templateComponents);
    this.setState(
      {
        ...this.state,
        values
      },
      () => {
        // make sure to call these after the change in state is complete, because they use those values
        const { disabled, hidden, hiddenParent } = this.checkConditionals();
        const { dirty, values } = this.getCalculatedValues();
        this.setState({
          ...this.state,
          dirty: dirty,
          disabled,
          errors: {},
          hidden,
          hiddenParent,
          values
        });
      }
    );
  }

  handleReset = () => {
    this.reset();
  };

  handleClick = (
    id: VariableIDHierarchy | string,
    e: React.MouseEvent<HTMLButtonElement, MouseEvent>
  ) => {
    e.preventDefault();
    //not used yet
  };

  handleBlur = (id: VariableIDHierarchy | string, error?: string) => {
    //not used yet
  };

  handleFocus = (id: VariableIDHierarchy | string) => {
    //not used yet
  };

  isItemInLayout(itemID: string) {
    return (
      this.props.layout.findIndex((component: LayoutItem) => {
        if (component.id === itemID) return true;
        if (component.children) {
          return this.isItemInTree(itemID, component.children);
        }
        return false;
      }) !== -1
    );
  }

  isItemInTree(itemID: string, items: LayoutItem[]): boolean {
    return (
      items.findIndex((item: LayoutItem) => {
        if (item.id === itemID) return true;
        if (item.children) {
          return this.isItemInTree(itemID, item.children);
        }
        return false;
      }) !== -1
    );
  }

  renderFormHead = (component?: Component) => {
    if (component?.props.hidden) return null;
    return (
      <FormHead
        description={this.props.description}
        descriptionClassName={this.props.classNames?.description}
        key={component?.props.id}
        style={component?.props.ui.attributes?.styles?.widget}
        title={this.props.title}
        titleClassName={this.props.classNames?.title}
        titleStyle={component?.props.ui.attributes?.styles?.title}
      />
    );
  };

  renderFormSubmit = (component?: Component) => {
    if (component?.props.hidden) return null;
    return (
      <div className="text-center" key={component?.props.id}>
        <LoadingMessages
          error={this.props.error}
          isUploading={this.isCurrentlyUploading()}
          success={this.props.success}
        />
        <FormSubmit
          className={component?.props.ui.attributes?.classNames?.widget}
          onClick={this.handleClick}
          title={component?.props.ui.attributes?.title as string}
        />
      </div>
    );
  };

  render() {
    const hasComponents = !!Object.keys(this.state.components).length;

    if (!hasComponents) {
      console.warn("No form components found.");
    }

    return (
      <LoaderOverlay
        isLoading={this.props.isLoading}
        fixed
        overflow={false}
        className="page-all"
      >
        <div
          className={classNames(
            "container form-container mb-5",
            this.props.classNames?.form
          )}
        >
          {!this.isItemInLayout(FORM_HEAD_ID) && this.renderFormHead()}
          <form onSubmit={this.handleSubmit} onReset={this.handleReset}>
            <FormContext.Provider
              value={{
                components: this.state.components,
                disabled: this.state.disabled,
                error: this.state.errors,
                formHeadRender: this.renderFormHead,
                formSubmitRender: this.renderFormSubmit,
                hidden: this.state.hidden,
                id: "/",
                onAddFiles: this.handleAddFiles,
                onBlur: this.handleBlur,
                onCancelFile: this.props.onCancelFile,
                onChange: this.handleChange,
                onClick: this.handleClick,
                onFocus: this.handleFocus,
                onRepeatChange: this.handleVariableRepeatChange,
                refs: this.componentRefs,
                templateComponents: this.state.templateComponents,
                variables: this.state.variables,
                value: this.state.values
              }}
            >
              {this.props.layout.map((item: LayoutItem) => {
                const component = this.state.components[scopeToID(item.id)];
                return (
                  <FormComponentRenderer
                    component={component}
                    id={component.props.id}
                    key={component.props.id}
                  />
                );
              })}
              {hasComponents &&
                !this.isItemInLayout(FORM_SUBMIT_ID) &&
                this.renderFormSubmit()}
            </FormContext.Provider>
          </form>
        </div>
      </LoaderOverlay>
    );
  }
}

export default FormDynamic;
